/**
 * This hook provides a list of assessment screening windows plus the list of all periods in the assessment calendar.
 * It uses the useCalendar hook to fetch the assessment calendar data to create the period portions of the list.
 * It uses the assignment service to fetch the current district assignment object and create the screening window portion of the list.
 * Combine them and return the list of assessment screening windows.
 */
import { useCalendar } from "./useCalendar";
import { useRosterInfo } from "./useRosterInfo";
import { Assignment } from "@/services/assignment/assignmentTypesAPI";

export enum AssessmentPeriodWindowType {
  DISTRICT_ASSIGNMENT = 'DistrictAssignment',
  PERIOD = 'Period'
}

export enum AssessmentWindowScreeningWindows {
  BOY = 'BOY',
  MOY = 'MOY',
  EOY = 'EOY'
}

export interface AssessmentPeriodItem {
  assignment: Assignment | null;
  assignmentType: string;
  periodId: number;
  current: boolean;
  startDate: string;
  endDate: string;
  tags: string[];
  screeningWindowType: AssessmentPeriodWindowType;
  window?: AssessmentWindowScreeningWindows | null;
  assignmentId: string | null;
}

export type AssessmentPeriodWindow = {
  loading: boolean;
  assessmentPeriodWindows: AssessmentPeriodItem[];
}

function getMostCommonGrade(students: any[]): string {
  if (!students || students.length === 0) return '';

  const gradeCount = new Map<number, number>();
  let maxCount = 0;
  let mostCommonGrade = 0;

  for (const student of students) {
    const count = (gradeCount.get(student.grade) || 0) + 1;
    gradeCount.set(student.grade, count);

    if (count > maxCount) {
      maxCount = count;
      mostCommonGrade = student.grade;
    }
  }

  return gradeNumberTogradeName[mostCommonGrade as keyof typeof gradeNumberTogradeName] || '';
}

const gradeNumberTogradeName = {
  0: 'KINDERGARTEN',
  1: 'FIRST',
  2: 'SECOND',
  3: 'THIRD',
  4: 'FOURTH',
  5: 'FIFTH',
  6: 'SIXTH',
  7: 'SEVENTH',
  8: 'EIGHTH',
  9: 'NINTH',
}

export const useAssessmentPeriodWindows = (): AssessmentPeriodWindow => {
  const { assessmentCalendar, assessmentDistrictWindows, loading, schoolYearCalendar } = useCalendar();
  const rosterData = useRosterInfo()

  const assignmentsByGrade = assessmentDistrictWindows?.assignment.reduce((acc: Record<string, any[]>, assignment: any) => {
    const grade = assignment.grade;
    if (!acc[grade]) {
      acc[grade] = [];
    }
    acc[grade].push(assignment);
    return acc;
  }, {});

  let mostCommonGrade = '';
  if (rosterData && rosterData.students) {
    mostCommonGrade = getMostCommonGrade(rosterData.students);
  }

  if (!assessmentCalendar && loading) {
    return {
      loading,
      assessmentPeriodWindows: [],
    };
  }
  if (!assessmentCalendar) {
    return {
      loading,
      assessmentPeriodWindows: [],
    };
  }
  if (!assessmentDistrictWindows) {
    return {
      loading,
      assessmentPeriodWindows: [],
    };
  }

  const today = new Date();

  const periodWindows = (assessmentCalendar.periods ?? []).map((period) => {
    const periodStart = new Date(period.startDate);
    const periodEnd = new Date(period.endDate);
    const isPeriodItselfCurrent = today >= periodStart && today <= periodEnd;

    let window: AssessmentWindowScreeningWindows | null = null;
    if (period.periodIdentifier === assessmentCalendar.boyWindow) {
      window = AssessmentWindowScreeningWindows.BOY;
    } else if (period.periodIdentifier === assessmentCalendar.moyWindow) {
      window = AssessmentWindowScreeningWindows.MOY;
    } else if (period.periodIdentifier === assessmentCalendar.eoyWindow) {
      window = AssessmentWindowScreeningWindows.EOY;
    }

    return {
      assignment: null,
      assignmentType: period.name,
      periodId: period.periodIdentifier,
      current: isPeriodItselfCurrent,
      startDate: period.startDate,
      endDate: period.endDate,
      tags: period.tags,
      screeningWindowType: AssessmentPeriodWindowType.PERIOD,
      window: window || null,
      assignmentId: null
    };
  });

  const assessmentDistrictWindow = [...(assignmentsByGrade?.[mostCommonGrade] || [])].sort((a, b) => Number(a.periodId) - Number(b.periodId));

  const assignmentWindows = assessmentDistrictWindow.map((a: Assignment) => {
    const start = new Date(a.dateFrom!);
    const end = new Date(a.dateTo!);
    const isCurrent = today >= start && today <= end;

    let window: AssessmentWindowScreeningWindows | null = null;
    if (Number(a.periodId) === assessmentCalendar.boyWindow) {
      window = AssessmentWindowScreeningWindows.BOY;
    } else if (Number(a.periodId) === assessmentCalendar.moyWindow) {
      window = AssessmentWindowScreeningWindows.MOY;
    } else if (Number(a.periodId) === assessmentCalendar.eoyWindow) {
      window = AssessmentWindowScreeningWindows.EOY;
    }

    return {
      assignment: a,
      assignmentType: a.assignmentType || '',
      periodId: Number(a.periodId),
      current: isCurrent,
      startDate: a.dateFrom!.split(' ')[0],
      endDate: a.dateTo!.split(' ')[0],
      tags: a.tags?.[0] || [],
      screeningWindowType: AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT,
      window,
      assignmentId: a.assignmentId
    };
  });

  const currentAssignment = assignmentWindows.find(a => a.current === true);
  const currentPeriod = periodWindows.find(p => p.current === true);
  const currentWindow = currentAssignment || currentPeriod;

  const remainingAssignments = (currentWindow === currentAssignment)
    ? assignmentWindows.filter(a => a !== currentAssignment)
    : assignmentWindows;

  const remainingPeriods = (currentWindow === currentPeriod)
    ? periodWindows.filter(p => p !== currentPeriod)
    : periodWindows;

  const orderedWindows = [
    ...(currentWindow ? [currentWindow] : []),
    ...remainingAssignments,
    ...remainingPeriods
  ];

  return {
    loading,
    assessmentPeriodWindows: orderedWindows as AssessmentPeriodItem[],
  };
};
